import {ControlledAutocompleteSelect} from '@hm/fundamental'
import {isNull} from 'lodash'
import React, {useCallback, useEffect, useMemo} from 'react'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {isEmptySelectedValue, mapEquipmentOptions, mapSelectedValue} from '../../../helpers'
import {
  SamplingPointEquipmentType,
  EquipmentOption,
  SamplingPointTypeOption,
  UpmDepartment
} from '../../../types'
import {SamplingPointsTableFormValues} from '../SamplingPointsTableForm.types'
const complexStylingClasses =
  "w-full min-w-0 [&_[data-cy$='-input']]:w-full [&_[data-cy$='-input']]:min-w-0 [&_[data-cy$='-input']]:overflow-hidden [&_[data-cy$='-input']]:text-ellipsis [&_[data-cy$='-input']]:whitespace-nowrap [&_[data-cy$='-clear']]:h-20px [&_[data-cy$='-clear']]:w-20px [&_[data-cy$='-clear']]:min-w-0 [&_[data-cy$='-clear']]:px-0"
export interface SamplingPointTypeEquipmentFormProps {
  rowId: number
  samplingPointTypeOptions: SamplingPointTypeOption[]
  assetOptions: EquipmentOption[]
  grindingCircuitOptions: EquipmentOption[]
  storageOptions: EquipmentOption[]
  upmDepartmentOptions: UpmDepartment[]
  isSaving: boolean
  field?: 'type' | 'equipment' | 'upmDepartment'
}

export const SamplingPointTypeEquipmentForm: React.FC<SamplingPointTypeEquipmentFormProps> = ({
  rowId,
  samplingPointTypeOptions,
  assetOptions,
  grindingCircuitOptions,
  storageOptions,
  upmDepartmentOptions,
  isSaving,
  field
}) => {
  const {t} = useTranslation()
  const {control, clearErrors, setError, setValue, trigger} =
    useFormContext<SamplingPointsTableFormValues>()

  const typeFieldName = `rowsById.${rowId}.typeOptionId` as const
  const equipmentFieldName = `rowsById.${rowId}.equipmentOptionKey` as const
  const upmDepartmentFieldName = `rowsById.${rowId}.upmDepartmentOptionId` as const

  const selectedTypeOptionId = useWatch({control, name: typeFieldName})
  const watchedEquipmentValue = useWatch({control, name: equipmentFieldName})

  const selectedTypeOption = useMemo(
    () => samplingPointTypeOptions.find((option) => option.id === selectedTypeOptionId) ?? null,
    [samplingPointTypeOptions, selectedTypeOptionId]
  )

  const equipmentOptions = useMemo(() => {
    if (isNull(selectedTypeOption)) {
      return []
    }

    if (selectedTypeOption.type === SamplingPointEquipmentType.asset) {
      return [...assetOptions, ...grindingCircuitOptions].sort((current, next) =>
        current.label.localeCompare(next.label)
      )
    }

    if (selectedTypeOption.type === SamplingPointEquipmentType.storage) {
      return storageOptions
    }

    return []
  }, [selectedTypeOption, assetOptions, grindingCircuitOptions, storageOptions])

  const upmDepartmentSelectValues = useMemo(
    () =>
      upmDepartmentOptions.map((upmDepartmentOption) => ({
        value: upmDepartmentOption.id,
        label: upmDepartmentOption.label
      })),
    [upmDepartmentOptions]
  )

  const handleChangeTypeOptions = useCallback(
    (newTypeOptionIdValues: (string | number)[], onChange: (value: number | null) => void) => {
      const newTypeOptionIdValue = newTypeOptionIdValues[0]
      if (isEmptySelectedValue(newTypeOptionIdValue)) {
        onChange(null)
        setValue(equipmentFieldName, null, {shouldTouch: true, shouldDirty: true})
        clearErrors(equipmentFieldName)
        return
      }

      const newTypeOptionId = Number(newTypeOptionIdValue)

      if (Number.isNaN(newTypeOptionId)) {
        onChange(null)
        setValue(equipmentFieldName, null, {shouldTouch: true, shouldDirty: true})
        clearErrors(equipmentFieldName)
        return
      }

      const newTypeOption =
        samplingPointTypeOptions.find((option) => option.id === newTypeOptionId) ?? null
      const previousEquipmentType = selectedTypeOption?.type
      const newEquipmentType = newTypeOption?.type
      onChange(newTypeOptionId)

      if (
        newTypeOptionId === null ||
        (previousEquipmentType && previousEquipmentType !== newEquipmentType)
      ) {
        setValue(equipmentFieldName, null, {shouldTouch: true, shouldDirty: true})
      }
    },
    [setValue, clearErrors, samplingPointTypeOptions, equipmentFieldName, selectedTypeOption]
  )

  const isEquipmentDropdownDisabled =
    isSaving ||
    isNull(selectedTypeOption) ||
    selectedTypeOption.type === SamplingPointEquipmentType.other

  useEffect(() => {
    if (!isEquipmentDropdownDisabled && isNull(watchedEquipmentValue)) {
      setError(equipmentFieldName, {
        type: 'manual',
        message: t('error.required')
      })
    } else {
      clearErrors(equipmentFieldName)
    }
  }, [
    isEquipmentDropdownDisabled,
    selectedTypeOption,
    watchedEquipmentValue,
    equipmentFieldName,
    setError,
    clearErrors,
    t
  ])

  const typeSelect = (
    <Controller
      name={typeFieldName}
      control={control}
      render={({field: {value, onChange}}) => (
        <div
          className={complexStylingClasses}
          data-style-id="sampling-point-autocomplete"
          data-test-id="sampling_point_type_cell"
        >
          <ControlledAutocompleteSelect
            variant="small"
            label={t('samplingPointsSettings.samplingPointType')}
            options={samplingPointTypeOptions.map((typeOption) => ({
              value: typeOption.id,
              label: typeOption.label
            }))}
            selectedValues={mapSelectedValue(value)}
            isDisabled={isSaving}
            onChange={(newTypeOptionIdValues) =>
              handleChangeTypeOptions(newTypeOptionIdValues, onChange)
            }
            data-cy="sampling_point_form_type_autocomplete"
          />
        </div>
      )}
    />
  )

  const equipmentSelect = (
    <Controller
      name={equipmentFieldName}
      control={control}
      render={({field: {value}, fieldState: {error, isTouched}}) => (
        <div
          className="w-full min-w-0 [&_[data-cy$='-input']]:w-full [&_[data-cy$='-input']]:min-w-0 [&_[data-cy$='-input']]:overflow-hidden [&_[data-cy$='-input']]:text-ellipsis [&_[data-cy$='-input']]:whitespace-nowrap [&_[data-cy$='-clear']]:h-20px [&_[data-cy$='-clear']]:w-20px [&_[data-cy$='-clear']]:min-w-0 [&_[data-cy$='-clear']]:px-0"
          data-test-id="sampling_point_equipment_cell"
        >
          <ControlledAutocompleteSelect
            variant="small"
            label={t('samplingPointsSettings.samplingPointEquipment')}
            options={mapEquipmentOptions(equipmentOptions)}
            selectedValues={mapSelectedValue(value)}
            error={error?.message}
            isTouched={isTouched}
            isDisabled={isEquipmentDropdownDisabled}
            onChange={(selectedEquipmentValues) => {
              const selectedEquipmentValue = selectedEquipmentValues[0]
              setValue(
                equipmentFieldName,
                isEmptySelectedValue(selectedEquipmentValue)
                  ? null
                  : String(selectedEquipmentValue),
                {shouldValidate: true, shouldTouch: true, shouldDirty: true}
              )
              void trigger(equipmentFieldName)
            }}
            data-cy="sampling_point_form_equipment_autocomplete"
          />
        </div>
      )}
      rules={{
        validate: (equipmentOptionKey) => {
          if (!isEquipmentDropdownDisabled && isNull(equipmentOptionKey)) {
            return t('error.required')
          }
          return true
        }
      }}
    />
  )

  const upmDepartmentSelect = (
    <Controller
      name={upmDepartmentFieldName}
      control={control}
      render={({field: {value, onChange}, fieldState: {error, isTouched}}) => (
        <div
          className="w-full min-w-0 [&_[data-cy$='-input']]:w-full [&_[data-cy$='-input']]:min-w-0 [&_[data-cy$='-input']]:overflow-hidden [&_[data-cy$='-input']]:text-ellipsis [&_[data-cy$='-input']]:whitespace-nowrap [&_[data-cy$='-clear']]:h-20px [&_[data-cy$='-clear']]:w-20px [&_[data-cy$='-clear']]:min-w-0 [&_[data-cy$='-clear']]:px-0"
          data-test-id="sampling_point_upm_department_cell"
        >
          <ControlledAutocompleteSelect
            variant="small"
            label={t('samplingPointsSettings.samplingPointUpmDepartment')}
            options={upmDepartmentSelectValues}
            selectedValues={mapSelectedValue(value)}
            error={error?.message}
            isTouched={isTouched}
            onChange={(selectedUpmDepartmentValues) => {
              const selectedUpmDepartmentValue = selectedUpmDepartmentValues[0]
              onChange(
                isEmptySelectedValue(selectedUpmDepartmentValue) ? null : selectedUpmDepartmentValue
              )
            }}
            data-cy="sampling_point_form_upm_department_autocomplete"
          />
        </div>
      )}
    />
  )

  switch (field) {
    case 'type':
      return <>{typeSelect}</>
    case 'equipment':
      return <>{equipmentSelect}</>
    case 'upmDepartment':
      return <>{upmDepartmentSelect}</>
    default:
      break
  }
}
